import { useGameBoard } from "@/hooks/useGameBoard";
import { useRandomizedWords } from "@/hooks/useRandomizedWords";
import { WordCards } from "./WordCards";
import { GameControls } from "./GameControls";
import { Timer } from "./Timer";
import { WordPreviewScreen } from "./screens/WordPreviewScreen";
import type { Word, LogicRule } from "@/lib/game-utils";
import { useIsMobile } from "@/hooks/use-mobile";
import { GameHeader } from "./GameHeader";
import { useState, useEffect } from "react";
import { HelpOverlay } from "./HelpOverlay";

interface GameBoardProps {
  words: Word[];
  logicRules: LogicRule[];
  targetOrder: Word[];
  targetRule: LogicRule;
  onComplete: (
    result: "perfect" | "wrong", 
    solveTime: number,
    selectedWords: Array<{ word: Word; order: number }>,
    selectedRule: LogicRule,
    filteredRules: LogicRule[]
  ) => void;
  isReplay?: boolean;
  externalDataUrl?: string;
}

export function GameBoard({ 
  words, 
  logicRules, 
  targetOrder, 
  targetRule, 
  onComplete, 
  isReplay = false,
  externalDataUrl 
}: GameBoardProps) {
  const isMobile = useIsMobile();
  const [showHelp, setShowHelp] = useState(true);
  
  // Hide help after 12 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowHelp(false);
    }, 12000);
    
    return () => clearTimeout(timer);
  }, []);

  // Use our hook to get randomized words - this ensures consistent randomization
  const displayWords = useRandomizedWords(words);

  const {
    elapsedTime,
    selectedWords,
    selectedRule,
    handleWordClick,
    handleSubmit,
    setElapsedTime,
    showingPreview,
    handleRevealResults
  } = useGameBoard(
    displayWords,
    [targetRule], // Only provide the target rule
    targetOrder,
    targetRule, 
    (result, solveTime, selectedWords, selectedRule, filteredRules) => {
      onComplete(result, solveTime, selectedWords, selectedRule, filteredRules);
    },
    externalDataUrl
  );

  const onWordClickHandler = (word: Word) => {
    handleWordClick(word);
  };

  // Handle explicit Lock In button submission
  const handleLockInSubmit = () => {
    console.log("Lock In button clicked, submitting game explicitly");
    // Pass true for isExplicitSubmit
    handleSubmit();  // Remove the argument here
  };

  // If showing preview, display the WordPreviewScreen
  if (showingPreview && selectedRule) {
    return (
      <WordPreviewScreen
        selectedWords={selectedWords}
        selectedRule={selectedRule}
        onRevealResults={handleRevealResults}
      />
    );
  }

  return (
    <div className="space-y-2 sm:space-y-3 relative">
      <div className="container mx-auto px-4 py-2 flex flex-col items-center">
        <GameHeader />
      </div>
      
      <div className="w-full max-w-2xl mx-auto relative">
        <div className="flex justify-center px-4">
          {/* Only show the target rule */}
          <div className="card-container">
            <div className="card-base logic-card bg-black text-white">
              <div className="flex flex-col items-center justify-center w-full px-2">
                <h3 className="logic-rule-title mb-1">
                  {targetRule.name}
                </h3>
                
                {targetRule.spectrum && targetRule.spectrum[0] && targetRule.spectrum[1] && (
                  <div className="logic-rule-spectrum-labels">
                    <span>{targetRule.spectrum[0]}</span>
                    <span className="logic-rule-spectrum-separator mx-1"><i>to</i></span>
                    <span>{targetRule.spectrum[1]}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        
        {/* Help overlay */}
        <HelpOverlay showHelp={showHelp && !isReplay} />
      </div>
      
      <Timer 
        onTimeUpdate={setElapsedTime} 
        disabled={isReplay}
      />
      
      <div className="bg-gray-50 p-4 sm:p-6 rounded-lg shadow-sm mt-2 mb-4">
        <WordCards 
          words={displayWords}
          selectedWords={selectedWords} 
          onWordClick={onWordClickHandler}
          onSubmit={handleLockInSubmit} // Pass the explicit submission handler
          selectedRule={true} // Always true since only one rule
          disabled={isReplay}
        />
      </div>
      
      <div className="flex justify-center mt-2">
        <GameControls
          selectedWords={selectedWords}
          selectedRule={targetRule} // Always the target rule
          words={displayWords}
          onSubmit={handleLockInSubmit} // Pass the explicit submission handler
          isReplay={isReplay}
        />
      </div>
    </div>
  );
}
